import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import { PlaceholderPage } from '@/components/common/placeholder-page';
import { AppShell } from '@/components/layout/app-shell';
import { DEALER_PORTAL_PAGE_NAME, PRODUCT_MANAGEMENT_PAGE_NAME } from '@/config/page-names';
import { isFeatureEnabled, whenEnabled } from '@/config/preview-features';
import { routes } from '@/config/routes';
import { ACTIVITY_LOG_PAGE_NAME } from '@/features/activity-log';
import { ADMIN_USERS_PAGE_NAME, ADMIN_USERS_ROLES } from '@/features/admin-users';
import { CUSTOMERS_PAGE_NAME, CUSTOMERS_ROLES } from '@/features/customers';
import { DEALERS_ROLES, USER_MANAGEMENT_PAGE_NAME } from '@/features/dealers';
import { DEVICES_PAGE_NAME } from '@/features/devices';
import { DISCOUNTS_PAGE_NAME, DISCOUNTS_ROLES } from '@/features/discounts';
import { NOTIFICATIONS_PAGE_NAME } from '@/features/notifications';
import { ORDERS_PAGE_NAME, ORDERS_ROLES } from '@/features/orders';
import { PAYMENTS_PAGE_NAME, PAYMENTS_ROLES } from '@/features/payments';
import { REPORTS_PAGE_NAME, REPORTS_ROLES } from '@/features/reports';
import { REQUESTS_PAGE_NAME, REQUESTS_ROLES } from '@/features/requests';
import { SUBSCRIPTIONS_PAGE_NAME } from '@/features/subscriptions';
import { INTERNAL_ROLES } from '@/types';

import { RequireAuth } from './guards/require-auth';
import { RequireDashboardAccess } from './guards/require-dashboard-access';
import { RequireRole } from './guards/require-role';
import { lazyRoute as lazy } from './lazy-route';

/**
 * Built by a function, not held as a literal, because which routes exist depends on the release
 * scope (ADR-0034): a capability that is not enabled contributes **no** route, so its URL falls
 * through to the shell's Not Found rather than rendering a placeholder or redirecting. The app
 * builds it once, at boot; tests build it per render, after `setPreviewFeatures`.
 */
export function buildProtectedRoutes(): RouteObject[] {
  return [
    {
      element: <RequireAuth />,
      children: [
        {
          element: <AppShell />,
          children: [
            {
              element: <RequireDashboardAccess />,
              children: [
                {
                  path: routes.dashboard,
                  lazy: lazy(() => import('@/pages/dashboard/dashboard.page'), 'DashboardPage'),
                },
              ],
            },
            {
              // Ungated on purpose (ADR-0028): the destination for roles with no permitted module.
              // Guarding it would leave those roles with nowhere at all to land.
              path: routes.noModules,
              element: <PlaceholderPage title="No modules available" />,
            },

            ...whenEnabled('dealers', [
              {
                // The dealer's portal (feature 120817, ADO 120818 §G). Its modules are their own
                // stories (120877, 121594, 121597); a module not yet landed holds its place with a
                // placeholder, so sign-in, the landing redirect and the sidebar work end to end.
                element: <RequireRole roles={['dealer']} pageName={DEALER_PORTAL_PAGE_NAME} />,
                children: [
                  {
                    path: routes.dealerPortalDevices,
                    lazy: lazy(
                      () => import('@/pages/dealer/dealer-devices.page'),
                      'DealerDevicesHubPage',
                    ),
                    children: [
                      {
                        index: true,
                        element: <Navigate to={routes.dealerPortalDevicesUnassigned} replace />,
                      },
                      {
                        path: 'unassigned',
                        lazy: lazy(
                          () => import('@/pages/dealer/dealer-devices.page'),
                          'DealerUnassignedDevicesPage',
                        ),
                      },
                      {
                        path: 'my-devices',
                        lazy: lazy(
                          () => import('@/pages/dealer/dealer-devices.page'),
                          'DealerMyDevicesPage',
                        ),
                      },
                      {
                        path: 'sold',
                        lazy: lazy(
                          () => import('@/pages/dealer/dealer-devices.page'),
                          'DealerSoldDevicesPage',
                        ),
                      },
                    ],
                  },
                  {
                    // Full-page sibling of the hub (no tabs; grilling G10): Back and every
                    // redirect return to My Devices.
                    path: routes.dealerPortalDevice(),
                    lazy: lazy(
                      () => import('@/pages/dealer/dealer-device-detail.page'),
                      'DealerDeviceDetailPage',
                    ),
                  },
                  {
                    path: routes.dealerPortalOrders,
                    lazy: lazy(
                      () => import('@/pages/dealer/orders/orders-list.page'),
                      'DealerOrdersListPage',
                    ),
                  },
                  {
                    path: routes.dealerPortalOrder(),
                    lazy: lazy(
                      () => import('@/pages/dealer/orders/order-detail.page'),
                      'DealerOrderDetailPage',
                    ),
                  },
                  {
                    path: routes.dealerPortalProducts,
                    element: <PlaceholderPage title="Products" />,
                  },
                  {
                    path: routes.dealerPortalCustomers,
                    element: <PlaceholderPage title="Customers" />,
                  },
                  {
                    // Reserved for story 121599 (feature 122141); Device Sold rows land here (grilling G1).
                    path: routes.dealerPortalCustomerDevices(),
                    element: <PlaceholderPage title="Customers" />,
                  },
                ],
              },
            ]),

            {
              element: <RequireRole roles={ORDERS_ROLES} pageName={ORDERS_PAGE_NAME} />,
              children: [
                {
                  path: '/orders',
                  lazy: lazy(() => import('@/pages/orders/orders-list.page'), 'OrdersListPage'),
                },
                {
                  path: '/orders/:id',
                  lazy: lazy(() => import('@/pages/orders/order-detail.page'), 'OrderDetailPage'),
                },
                {
                  path: '/orders/:id/edit',
                  lazy: lazy(() => import('@/pages/orders/order-edit.page'), 'OrderEditPage'),
                },
              ],
            },

            {
              element: <RequireRole roles={CUSTOMERS_ROLES} pageName={CUSTOMERS_PAGE_NAME} />,
              children: [
                {
                  path: '/customers',
                  lazy: lazy(
                    () => import('@/pages/customers/customers-list.page'),
                    'CustomersListPage',
                  ),
                },
                {
                  // Full-page create form — sibling of `/customers/:id` (not a hub child) so it
                  // renders without the detail tab shell; matched before the `:id` param route.
                  path: '/customers/new',
                  lazy: lazy(
                    () => import('@/pages/customers/customer-create.page'),
                    'CustomerCreatePage',
                  ),
                },
                {
                  // Full-page edit form — sibling of the `:id` detail hub (React Router ranks
                  // `/customers/:id/edit` above `/customers/:id`, so it renders without the tab shell).
                  path: '/customers/:id/edit',
                  lazy: lazy(
                    () => import('@/pages/customers/customer-edit.page'),
                    'CustomerEditPage',
                  ),
                },
                ...whenEnabled('device-telemetry', [
                  {
                    // A full-page SIBLING of the `:id` detail hub, like `/customers/:id/edit` above:
                    // Figma `1950:869003` draws no customer header and no tab bar, just the page's own
                    // `‹ Back`. React Router reaches it unambiguously — the hub's `devices` child
                    // matches only the exact tab path, so nothing else claims this URL.
                    path: '/customers/:id/devices/:deviceId',
                    lazy: lazy(
                      () => import('@/pages/customers/customer-device-detail.page'),
                      'CustomerDeviceDetailPage',
                    ),
                  },
                ]),
                {
                  path: '/customers/:id',
                  lazy: lazy(
                    () => import('@/pages/customers/customer-detail.page'),
                    'CustomerDetailPage',
                  ),
                  children: [
                    { index: true, element: <Navigate to="order-history" replace /> },
                    {
                      path: 'order-history',
                      lazy: lazy(
                        () => import('@/pages/customers/order-history.page'),
                        'OrderHistoryPage',
                      ),
                    },
                    {
                      path: 'devices',
                      lazy: lazy(
                        () => import('@/pages/customers/devices.page'),
                        'CustomerDevicesPage',
                      ),
                    },
                    ...whenEnabled('device-telemetry', [
                      {
                        path: 'device-alerts',
                        lazy: lazy(
                          () => import('@/pages/customers/device-alerts.page'),
                          'CustomerDeviceAlertsPage',
                        ),
                      },
                    ]),
                    ...whenEnabled('subscriptions', [
                      {
                        path: 'subscription-details',
                        lazy: lazy(
                          () => import('@/pages/customers/subscription-details.page'),
                          'CustomerSubscriptionsPage',
                        ),
                      },
                    ]),
                    ...whenEnabled('support', [
                      {
                        path: 'order-support',
                        element: <PlaceholderPage title="Order Support History" />,
                      },
                      {
                        path: 'device-support',
                        element: <PlaceholderPage title="Device Support History" />,
                      },
                    ]),
                    ...whenEnabled('activity-log', [
                      {
                        path: 'activity-log',
                        lazy: lazy(
                          () => import('@/pages/customers/activity-log.page'),
                          'CustomerActivityLogPage',
                        ),
                      },
                    ]),
                    ...whenEnabled('customer-map', [
                      {
                        path: 'map',
                        lazy: lazy(() => import('@/pages/customers/map.page'), 'CustomerMapPage'),
                      },
                      {
                        // A SIBLING of `map`, not a child: the Map view unmounts so only one
                        // <MapCanvas> is ever live, while <ModuleTabs> keeps the Map tab active off the
                        // `map/` prefix and the hub keeps the customer's name in the header
                        // (`113602`; grilling Q4).
                        path: 'map/fields/new',
                        lazy: lazy(
                          () => import('@/pages/customers/field-mapping.page'),
                          'FieldMappingPage',
                        ),
                      },
                      {
                        // The edit twin: one page component, told which Field to seed from by the
                        // path. A sibling of `map/fields/new`, not nested under it.
                        path: 'map/fields/:fieldId/edit',
                        lazy: lazy(
                          () => import('@/pages/customers/field-mapping.page'),
                          'FieldMappingPage',
                        ),
                      },
                      {
                        // A hub CHILD, unlike the details page above: `2039:512886` is the *expanded*
                        // state of a surface that also has a normal one (its control stack draws the
                        // `CornersIn` collapse glyph), so the customer header and `<ModuleTabs>` stay —
                        // and the tab bar lights **Devices**, off the `devices` prefix, which is where
                        // the admin came from and where Cancel returns (grilling Q3).
                        path: 'devices/:deviceId/remap',
                        lazy: lazy(
                          () => import('@/pages/customers/remap-device.page'),
                          'RemapDevicePage',
                        ),
                      },
                    ]),
                  ],
                },
              ],
            },

            {
              element: <RequireRole roles={DISCOUNTS_ROLES} pageName={DISCOUNTS_PAGE_NAME} />,
              children: [
                {
                  path: routes.discounts,
                  lazy: lazy(
                    () => import('@/pages/discounts/discounts-list.page'),
                    'DiscountsListPage',
                  ),
                },
                {
                  path: routes.discountCreate,
                  lazy: lazy(
                    () => import('@/pages/discounts/discount-create.page'),
                    'DiscountCreatePage',
                  ),
                },
                {
                  path: routes.discount(),
                  lazy: lazy(
                    () => import('@/pages/discounts/discount-detail.page'),
                    'DiscountDetailPage',
                  ),
                },
              ],
            },

            {
              element: (
                <RequireRole
                  roles={['admin', 'super_admin']}
                  pageName={PRODUCT_MANAGEMENT_PAGE_NAME}
                />
              ),
              children: [
                {
                  path: routes.products,
                  lazy: lazy(() => import('@/pages/products/products-hub.page'), 'ProductsHubPage'),
                  children: [
                    { index: true, element: <Navigate to={routes.productsDevices} replace /> },
                    {
                      path: 'devices',
                      lazy: lazy(
                        () => import('@/pages/devices/devices-list.page'),
                        'DevicesListPage',
                      ),
                    },
                    {
                      path: 'components',
                      lazy: lazy(
                        () => import('@/pages/products/components-list.page'),
                        'ComponentsListPage',
                      ),
                    },
                    {
                      path: 'category',
                      lazy: lazy(
                        () => import('@/pages/products/categories-list.page'),
                        'CategoriesListPage',
                      ),
                    },
                    {
                      path: 'bundles',
                      lazy: lazy(
                        () => import('@/pages/products/bundles-list.page'),
                        'BundlesListPage',
                      ),
                    },
                  ],
                },
              ],
            },
            {
              // Internal roles only, so a dealer who types the URL is refused (ADO 120818 §G).
              element: (
                <RequireRole roles={INTERNAL_ROLES} pageName={PRODUCT_MANAGEMENT_PAGE_NAME} />
              ),
              children: [
                {
                  path: routes.productEdit(),
                  lazy: lazy(() => import('@/pages/products/product-edit.page'), 'ProductEditPage'),
                },
              ],
            },

            {
              element: (
                <RequireRole
                  roles={['admin', 'super_admin']}
                  pageName={PRODUCT_MANAGEMENT_PAGE_NAME}
                />
              ),
              children: [
                {
                  path: routes.productsDevicesNew,
                  lazy: lazy(
                    () => import('@/pages/devices/device-create.page'),
                    'DeviceCreatePage',
                  ),
                },
                {
                  path: routes.productsBundlesNew,
                  lazy: lazy(
                    () => import('@/pages/products/bundle-create.page'),
                    'BundleCreatePage',
                  ),
                },
                {
                  path: routes.productsBundlesEdit(),
                  lazy: lazy(() => import('@/pages/products/bundle-edit.page'), 'BundleEditPage'),
                },
                {
                  path: routes.productsDevicesEdit(),
                  lazy: lazy(() => import('@/pages/devices/device-edit.page'), 'DeviceEditPage'),
                },
                {
                  path: routes.productsComponentsNew,
                  lazy: lazy(
                    () => import('@/pages/components/component-create.page'),
                    'ComponentCreatePage',
                  ),
                },
                {
                  path: routes.productsComponentsEdit(),
                  lazy: lazy(
                    () => import('@/pages/components/component-edit.page'),
                    'ComponentEditPage',
                  ),
                },
              ],
            },

            {
              element: <RequireRole roles={REQUESTS_ROLES} pageName={REQUESTS_PAGE_NAME} />,
              children: [
                {
                  path: routes.requests,
                  lazy: lazy(() => import('@/pages/requests/request-hub.page'), 'RequestHubPage'),
                  children: [
                    { index: true, element: <Navigate to={routes.requestsInstallation} replace /> },
                    {
                      path: 'installation',
                      lazy: lazy(
                        () => import('@/pages/installations/installation-queue.page'),
                        'InstallationQueuePage',
                      ),
                    },
                    {
                      path: 'replacement',
                      lazy: lazy(
                        () => import('@/pages/requests/replacement-queue.page'),
                        'ReplacementQueuePage',
                      ),
                    },
                    {
                      path: 'warranty',
                      lazy: lazy(
                        () => import('@/pages/requests/warranty-queue.page'),
                        'WarrantyQueuePage',
                      ),
                    },
                  ],
                },
                {
                  path: routes.requestsInstallationMap(),
                  lazy: lazy(
                    () => import('@/pages/installations/mapped-devices.page'),
                    'MappedDevicesPage',
                  ),
                },
              ],
            },

            {
              // Internal roles only: a dealer's Device listing is `routes.dealerPortalDevices`
              // (ADO 120818 §G).
              element: <RequireRole roles={INTERNAL_ROLES} pageName={DEVICES_PAGE_NAME} />,
              children: [
                {
                  path: routes.devices,
                  lazy: lazy(() => import('@/pages/devices/devices-list.page'), 'DevicesListPage'),
                },
                ...whenEnabled('device-telemetry', [
                  {
                    path: routes.device(),
                    lazy: lazy(
                      () => import('@/pages/devices/device-detail.page'),
                      'DeviceDetailPage',
                    ),
                  },
                ]),
              ],
            },

            ...whenEnabled('support', [
              {
                path: routes.support,
                lazy: lazy(() => import('@/pages/support/support-hub.page'), 'SupportHubPage'),
                children: [
                  { index: true, element: <Navigate to={routes.supportOrders} replace /> },
                  { path: 'orders', element: <PlaceholderPage title="Support Tickets — Orders" /> },
                  {
                    path: 'devices',
                    element: <PlaceholderPage title="Support Tickets — Devices" />,
                  },
                ],
              },
              {
                path: '/support/:id',
                lazy: lazy(() => import('@/pages/support/ticket-detail.page'), 'TicketDetailPage'),
              },
            ]),

            ...whenEnabled('subscriptions', [
              {
                element: <RequireRole roles={INTERNAL_ROLES} pageName={SUBSCRIPTIONS_PAGE_NAME} />,
                children: [
                  {
                    path: routes.subscriptions,
                    lazy: lazy(
                      () => import('@/pages/subscriptions/subscriptions-list.page'),
                      'SubscriptionsListPage',
                    ),
                  },
                ],
              },
            ]),
            {
              element: <RequireRole roles={PAYMENTS_ROLES} pageName={PAYMENTS_PAGE_NAME} />,
              children: [
                {
                  path: '/payments',
                  lazy: lazy(
                    () => import('@/pages/payments/payments-list.page'),
                    'PaymentsListPage',
                  ),
                },
              ],
            },

            {
              element: <RequireRole roles={REPORTS_ROLES} pageName={REPORTS_PAGE_NAME} />,
              children: [
                {
                  path: routes.reports,
                  lazy: lazy(() => import('@/pages/reports/reports-hub.page'), 'ReportsHubPage'),
                  children: [
                    { index: true, element: <Navigate to={routes.reportOrderSummary} replace /> },
                    {
                      path: 'order-summary',
                      lazy: lazy(
                        () => import('@/pages/reports/order-summary-report.page'),
                        'OrderSummaryReportPage',
                      ),
                    },
                    {
                      path: 'products',
                      lazy: lazy(
                        () => import('@/pages/reports/products-report.page'),
                        'ProductsReportPage',
                      ),
                    },
                    ...whenEnabled('device-telemetry', [
                      {
                        path: 'device-activity',
                        lazy: lazy(
                          () => import('@/pages/reports/device-activity-report.page'),
                          'DeviceActivityReportPage',
                        ),
                      },
                    ]),
                    ...whenEnabled('subscriptions', [
                      {
                        path: 'subscriptions',
                        lazy: lazy(
                          () => import('@/pages/reports/subscriptions-report.page'),
                          'SubscriptionsReportPage',
                        ),
                      },
                    ]),
                  ],
                },
              ],
            },

            ...whenEnabled('notifications', [
              {
                // Internal roles only: the dealer portal has no notifications surface (ADO 120818 §G).
                element: <RequireRole roles={INTERNAL_ROLES} pageName={NOTIFICATIONS_PAGE_NAME} />,
                children: [
                  {
                    path: routes.notifications,
                    lazy: lazy(
                      () => import('@/pages/notifications/notifications.page'),
                      'NotificationsPage',
                    ),
                  },
                ],
              },
            ]),
            ...whenEnabled('activity-log', [
              {
                element: <RequireRole roles={INTERNAL_ROLES} pageName={ACTIVITY_LOG_PAGE_NAME} />,
                children: [
                  {
                    path: routes.activityLog,
                    lazy: lazy(
                      () => import('@/pages/activity-log/activity-log.page'),
                      'ActivityLogPage',
                    ),
                  },
                ],
              },
            ]),

            {
              element: <RequireRole roles={['admin', 'super_admin']} />,
              children: [
                {
                  path: '/configurations/tax',
                  lazy: lazy(
                    () => import('@/pages/configurations/tax-config.page'),
                    'TaxConfigPage',
                  ),
                },
                {
                  path: '/configurations/installation-fees',
                  lazy: lazy(
                    () => import('@/pages/configurations/installation-fees.page'),
                    'InstallationFeesPage',
                  ),
                },
                {
                  path: '/configurations/warranty-periods',
                  lazy: lazy(
                    () => import('@/pages/configurations/warranty-periods.page'),
                    'WarrantyPeriodsPage',
                  ),
                },
              ],
            },

            isFeatureEnabled('dealers')
              ? {
                  // User Management hub (feature 118478, ADR-0036). The outer guard admits
                  // Customer Support for the Dealers tab; Internal keeps its own admin-only guard.
                  element: (
                    <RequireRole roles={DEALERS_ROLES} pageName={USER_MANAGEMENT_PAGE_NAME} />
                  ),
                  children: [
                    {
                      path: routes.adminUsers,
                      lazy: lazy(
                        () => import('@/pages/user-management/user-management-hub.page'),
                        'UserManagementHubPage',
                      ),
                      children: [
                        {
                          index: true,
                          lazy: lazy(
                            () => import('@/pages/user-management/user-management-hub.page'),
                            'UserManagementIndex',
                          ),
                        },
                        {
                          element: (
                            <RequireRole
                              roles={ADMIN_USERS_ROLES}
                              pageName={ADMIN_USERS_PAGE_NAME}
                            />
                          ),
                          children: [
                            {
                              path: 'internal',
                              lazy: lazy(
                                () => import('@/pages/user-management/admin-users-list.page'),
                                'AdminUsersListPage',
                              ),
                            },
                          ],
                        },
                        {
                          path: 'dealers',
                          lazy: lazy(
                            () => import('@/pages/user-management/dealers-list.page'),
                            'DealersListPage',
                          ),
                        },
                      ],
                    },
                    {
                      // A sibling of the hub, not a child: the detail is full-page, with no
                      // User Management tab bar (ticket 09).
                      path: routes.dealer(),
                      lazy: lazy(
                        () => import('@/pages/user-management/dealer-detail.page'),
                        'DealerDetailPage',
                      ),
                      children: [
                        { index: true, element: <Navigate to="unassigned" replace /> },
                        {
                          path: 'unassigned',
                          lazy: lazy(
                            () => import('@/pages/user-management/dealer-devices.page'),
                            'DealerUnassignedDevicesPage',
                          ),
                        },
                        {
                          path: 'self-assigned',
                          lazy: lazy(
                            () => import('@/pages/user-management/dealer-devices.page'),
                            'DealerSelfAssignedDevicesPage',
                          ),
                        },
                        {
                          path: 'sold',
                          lazy: lazy(
                            () => import('@/pages/user-management/dealer-devices.page'),
                            'DealerSoldDevicesPage',
                          ),
                        },
                      ],
                    },
                  ],
                }
              : {
                  element: (
                    <RequireRole roles={ADMIN_USERS_ROLES} pageName={ADMIN_USERS_PAGE_NAME} />
                  ),
                  children: [
                    {
                      path: routes.adminUsers,
                      lazy: lazy(
                        () => import('@/pages/user-management/admin-users-list.page'),
                        'AdminUsersListPage',
                      ),
                    },
                  ],
                },
          ],
        },
        {
          // Outside the shell — Figma `1960:514590` is a full-bleed page — yet still below
          // `<RequireAuth>`, so a signed-out visitor is sent to login first. Hidden and mistyped
          // URLs both land here (ADR-0034).
          path: '*',
          lazy: lazy(() => import('@/pages/errors/not-found.page'), 'NotFoundPage'),
        },
      ],
    },
  ];
}

export const protectedRoutes: RouteObject[] = buildProtectedRoutes();
